import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Grid } from '@mui/material';
import { TcsmEpiApiState, TcsmStates } from '../../../root/tcsm-model';
import { Employee } from '../features/employment/employment-model';
import { ArcRoutes } from '../constants/routes';
import { InfoBarWrapper } from '../../../global/components/layout/info-bar-wrapper';
import { NameClientNumber } from '../../../global/components/name-client-number';
import { Breadcrumbs } from '../../../global/components/breadcrumbs';
import { AdminNav } from '../../../global/components/admin-nav';
import { GroupsNav } from '../../../global/components/groups-nav';
import { BillingAccountInfoSection } from '../features/billing-accounts/billing-account-info-section';
import { AnnualEnrollmentRoutes } from '../../annual-enrollment/constants/annual-enrollment-routes';
import AnnualEnrollmentProgressBar from '../../annual-enrollment/components/annual-enrollment-progress';
import { GenerateAdminQuickNavMenu, GenerateGroupsQuickNavMenu, GenerateInstitutionQuickNavMenu, NavOverwrites } from '../../../global/constants/navigation-lists';
import { InstitutionInfoSection } from '../features/institutions/institution-info-section';
import { GroupInfoSection } from './group-info-section';
import { InstitutionNav } from '../../../global/components/institution-nav';
import { InstitutionNameClientNumber } from '../../../global/components/institution-name-client-number';
import { BillingAccountNav } from '../../../global/components/billing-account-nav';
import { BillingAccountInfoHeader } from './billing-account-info-header';
import { shouldShowAeHeader } from '../../annual-enrollment/helpers/annual-enrollment-helpers';
import { TcsmSiteSettingsProps } from '../../../global/models/epi-model';
import { isDioAdmin } from '../../../global/helpers/auth-helpers';
import { useBasePath } from '../../../global/hooks/useBasePath';
import { GroupInfoHeader } from './group-info-header';
import { DiocesanAdministrator } from '../constants/constant-strings';


export function MapInfoSection() {

	const routeMatch = useRouteMatch();
	const selectedGroupId = useSelector<TcsmStates, number>(state => state.groupsState?.currentGroup?.group_id);
	const siteSettings = useSelector<TcsmStates, TcsmSiteSettingsProps>(state => state.epiProps?.siteSettingsState?.siteSettings);
	const language = useSelector<TcsmStates, string>(state => state.globalization.currentlanguage);
	const eeNavOptions = GenerateAdminQuickNavMenu(`${routeMatch.path}${ArcRoutes.PeopleRoot}`, language, siteSettings);
	const groupNavOptions = GenerateGroupsQuickNavMenu(`${routeMatch.path}${ArcRoutes.GroupsLanding}`, language, siteSettings);
	const institutionCn = useSelector<TcsmStates, string>(state => state?.institutionState?.institutionClientNumber);
	const isDiocesan = isDioAdmin(institutionCn);
	const overwrite: NavOverwrites = { matchPath: ArcRoutes.ReportsDiocesan, overwritePath: useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.ReportsDiocesan}`), hide: !isDiocesan };
	const institutionOptions = GenerateInstitutionQuickNavMenu(`${routeMatch.path}${ArcRoutes.InstitutionRoot}`, language, siteSettings, [overwrite]);
	const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const currentEmployee = useSelector<TcsmStates, Employee>(state => state.employmentState?.currentEmployee);
	const history = useHistory();
	const currentPath = window?.location?.pathname?.toLowerCase();
	const roles = useSelector<TcsmStates, string[]>(state => state?.authenticatedState?.roles);
	const isDioApprover = !!roles.includes(DiocesanAdministrator) && currentPath?.includes(`${ArcRoutes.DioApproverEnrollment}`);
	const isLifeEventCoverageForm = useSelector<TcsmStates, boolean>(state => state?.newHireEnrollmentState?.isLifeEventCoverageForm);


	const getAdminNavBasePath = (curPath: string) => {
		if (curPath?.includes(`${ArcRoutes.GroupsLanding}${ArcRoutes.BillingAccountsSearch}${ArcRoutes.Participants}`)) {
			return `${ArcRoutes.GroupsLanding}${ArcRoutes.BillingAccountsSearch}${ArcRoutes.Participants}`;
		}

		if (curPath?.includes(`${ArcRoutes.GroupsLanding}${ArcRoutes.GroupAdministrators}`)) {
			return `${ArcRoutes.GroupsLanding}${ArcRoutes.GroupAdministrators}`;
		}

		return `${ArcRoutes.PeopleRoot}`;
	};

	const getDioApproverTitle = () => {
		if (isLifeEventCoverageForm) {
			return epiProps?.newHireSettingsState?.epiContent?.dioceseApproverLifeEventTitle;
		}
		return epiProps?.newHireSettingsState?.epiContent?.dioceseApproverEnrollmentTitle;
	};

	const hideInfo = () => {
		const hiddenRoutes = [ArcRoutes.EmploymentTerminationConfirmation, ArcRoutes.ParticipantEnrollment]; // add more if necessary

		return hiddenRoutes.some(route => currentPath?.includes(route)) ||
			((!currentEmployee &&
				!eeNavOptions?.some(a => currentPath?.includes((a.value?.toLocaleLowerCase()))) &&
				!groupNavOptions?.some(a => currentPath?.includes((a.value?.toLocaleLowerCase())) || currentPath?.includes(a.fallbackMatch?.toLowerCase() || ' '))) &&
				!institutionOptions?.some(a => currentPath?.includes((a.value?.toLocaleLowerCase()))));
	};

	const hideBreadCrumbs = () => {
		const hiddenRoutesForBreadCrumbs = [AnnualEnrollmentRoutes.AnnualEnrollment, ArcRoutes.ParticipantEnrollment, ArcRoutes.QccoPlanAdoption, ArcRoutes.QccoPlanAmend];
		return hiddenRoutesForBreadCrumbs.some(route => currentPath?.includes(route));
	};

	const shouldShowNameClientNumber = () => {
		return !currentPath?.includes(ArcRoutes.NewHireEnrollment);
	};

	return (
		<>
			<InstitutionInfoSection />
			<BillingAccountInfoSection />
			<GroupInfoHeader basePath={`${routeMatch.path}${ArcRoutes.GroupsLanding}`} />

			{
				(!routeMatch.isExact && !hideBreadCrumbs()) &&
				<Breadcrumbs routeBreadcrumbs={epiProps?.siteSettingsState?.siteSettings?.routeBreadcrumbs} />
			}
			{
				!hideInfo() &&
				<InfoBarWrapper>
					<GroupInfoSection groupId={selectedGroupId} basePath={`${routeMatch.path}${ArcRoutes.GroupsLanding}`} />
					<BillingAccountInfoHeader basePath={`${routeMatch.path}${ArcRoutes.GroupsLanding}${ArcRoutes.BillingAccountsSearch}`} />					
					{ shouldShowNameClientNumber() && <NameClientNumber client={currentEmployee} clientNumberLabel={epiProps?.labelSettingState?.epiContent?.clientNumberLabel} isDioApprover={isDioApprover} dioApproverTitle={getDioApproverTitle()} /> }
					<InstitutionNameClientNumber />
					<Grid item md={4} className='infobar-nav'>
						<AdminNav basePath={`${routeMatch.path}${getAdminNavBasePath(currentPath)}`} />
						<GroupsNav basePath={`${routeMatch.path}${ArcRoutes.GroupsLanding}`} />
						<InstitutionNav basePath={`${routeMatch.path}${ArcRoutes.InstitutionRoot}`} />
						<BillingAccountNav basePath={`${routeMatch.path}${ArcRoutes.GroupsLanding}${ArcRoutes.BillingAccountsSearch}`} />

					</Grid>
					{
						shouldShowAeHeader(history) &&
						<h2>{epiProps?.annualEnrollmentSettingsState?.epiContent?.annualEnrollmentTitle}</h2>
					}
				</InfoBarWrapper>
			}
			<AnnualEnrollmentProgressBar basePath={`${routeMatch.path}${AnnualEnrollmentRoutes.AnnualEnrollment}`} />

		</>
	);
}
